/**
 * VHAKACHA SERVICE PROVIDERS  (web app)
 * ============================================================
 * Reading the operator behind a listing.
 *
 * WHY THIS IS A READ AND NOT A FIELD ON THE LISTING
 * ------------------------------------------------
 * The listing page shows a "Verified operator" badge, and the only honest
 * source for it is the provider's own `kycStatus`.
 *
 * Denormalizing that onto the listing — the way `company.name` is — would put
 * a verification flag on a document THE PROVIDER WRITES. firestore.rules
 * already refuses to let a provider write `ratingAverage` on its own listing
 * for exactly this reason: the one field a party has an incentive to forge is
 * the one it must not control. A stale copy would be the smaller problem; a
 * forgeable one is the real objection.
 *
 * `service_providers` is world-readable (`allow read: if true`), so this costs
 * one document read on a listing page and nothing anywhere else.
 * ============================================================
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Statuses that earn the badge.
 *
 * ONLY `FULLY_VERIFIED`. `TIER_1_APPROVED` means cleared to publish, which is
 * a lower bar — showing the same badge for both would tell a traveller that
 * documents have been checked when they have not.
 */
const VERIFIED_STATUSES = ['FULLY_VERIFIED'];

/**
 * The provider document, or null.
 *
 * Null on failure rather than throwing: a listing page whose operator record
 * cannot be read should still show the listing, minus a badge.
 */
export async function getProvider(providerId) {
  if (!providerId) return null;

  try {
    const snapshot = await getDoc(doc(db, 'service_providers', providerId));
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() };
  } catch (error) {
    console.error('Failed to read provider', providerId, error);
    return null;
  }
}

/** Has this operator cleared full verification? */
export function isVerifiedOperator(provider) {
  return VERIFIED_STATUSES.includes(provider?.kycStatus);
}
